import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import {
  X,
  Building2,
  Mail,
  Phone,
  MapPin,
  Globe,
  Hash,
  FileText,
} from '@/components/icons'
import { Button } from './ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from './ui/drawer'
import { PrntrCard } from './ui/prntr-input'
import { SelectRowField, TextRow } from './ui/prntr-fields'
import { DetailSkeleton } from './ui/skeleton-presets'
import { useCompanyGet, useCompanyUpdate } from '@/lib/hooks/company'

const timezones: string[] = (
  Intl as typeof Intl & { supportedValuesOf: (key: string) => string[] }
).supportedValuesOf('timeZone')

const timezoneOptions = timezones.map((tz) => ({ value: tz, label: tz }))

const CompanyFormSchema = z.object({
  name: z.string().min(1, 'Company name is required'),
  timezone: z.string().min(1, 'Timezone is required'),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  phone: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(),
  postalCode: z.string().optional(),
  legalName: z.string().optional(),
  registrationNumber: z.string().optional(),
  taxId: z.string().optional(),
})

type CompanyFormData = z.infer<typeof CompanyFormSchema>

const defaultFormData: CompanyFormData = {
  name: '',
  timezone: 'UTC',
  email: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  country: '',
  postalCode: '',
  legalName: '',
  registrationNumber: '',
  taxId: '',
}

interface CompanyInfoDrawerProps {
  isOpen: boolean
  onClose: () => void
}

export function CompanyInfoDrawer({ isOpen, onClose }: CompanyInfoDrawerProps) {
  const { data: company, isLoading } = useCompanyGet()
  const updateMutation = useCompanyUpdate()

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty, isValid },
    reset,
  } = useForm<CompanyFormData>({
    resolver: zodResolver(CompanyFormSchema),
    defaultValues: defaultFormData,
    mode: 'onChange',
  })

  useEffect(() => {
    if (isOpen) {
      if (company) {
        reset({
          name: company.name || '',
          timezone: company.timezone || 'UTC',
          email: company.email || '',
          phone: company.phone || '',
          address: company.address || '',
          city: company.city || '',
          state: company.state || '',
          country: company.country || '',
          postalCode: company.postalCode || '',
          legalName: company.legalName || '',
          registrationNumber: company.registrationNumber || '',
          taxId: company.taxId || '',
        })
      } else {
        reset(defaultFormData)
      }
    }
  }, [isOpen, company, reset])

  const handleFormSubmit = handleSubmit(async (data) => {
    try {
      await updateMutation.mutateAsync({
        name: data.name,
        timezone: data.timezone,
        // Cleared fields must be null, not undefined — Prisma ignores undefined
        // keys, so `|| undefined` would silently keep the old value.
        email: data.email || null,
        phone: data.phone || null,
        address: data.address || null,
        city: data.city || null,
        state: data.state || null,
        country: data.country || null,
        postalCode: data.postalCode || null,
        legalName: data.legalName || null,
        registrationNumber: data.registrationNumber || null,
        taxId: data.taxId || null,
      })
      toast.success('Company info updated successfully')
      onClose()
    } catch (error) {
      toast.error('Failed to update company info')
      console.error(error)
    }
  })

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent className="bg-surface-L0 shadow-lg max-h-[85dvh]">
        {/* Header */}
        <DrawerHeader className="flex items-center justify-between p-4">
          <DrawerTitle className="font-semibold text-lg text-content-primary-bold">
            Edit Company Info
          </DrawerTitle>
          <Button variant="secondary" size="icon" roundness="round" className="size-8" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </DrawerHeader>

        {/* Form Content */}
        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-0"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {isLoading ? (
            <DetailSkeleton rows={6} />
          ) : (
            <form className="flex flex-col gap-3 p-2">
              <PrntrCard title="Company">
                <TextRow nullable={false}
                  control={control}
                  name="name"
                  label="Company Name *"
                  placeholder="Enter company name"
                  iconLeft={<Building2 />}
                  error={errors.name?.message}
                />
                <SelectRowField
                  control={control}
                  name="timezone"
                  label="Timezone *"
                  options={timezoneOptions}
                  placeholder="Select timezone"
                  error={errors.timezone?.message}
                />
                <TextRow nullable={false}
                  control={control}
                  name="email"
                  label="Email"
                  type="email"
                  placeholder="company@example.com"
                  iconLeft={<Mail />}
                  error={errors.email?.message}
                />
                <TextRow nullable={false}
                  control={control}
                  name="phone"
                  label="Phone"
                  type="tel"
                  inputMode="tel"
                  placeholder="+1 234 567 8900"
                  iconLeft={<Phone />}
                />
              </PrntrCard>

              <PrntrCard title="Address">
                <TextRow nullable={false}
                  control={control}
                  name="address"
                  label="Street Address"
                  placeholder="123 Main Street"
                  iconLeft={<MapPin />}
                />
                <TextRow nullable={false} control={control} name="city" label="City" placeholder="City" />
                <TextRow nullable={false} control={control} name="state" label="State/Province" placeholder="State" />
                <TextRow nullable={false}
                  control={control}
                  name="country"
                  label="Country"
                  placeholder="Country"
                  iconLeft={<Globe />}
                />
                <TextRow nullable={false} control={control} name="postalCode" label="Postal Code" placeholder="12345" />
              </PrntrCard>

              <PrntrCard title="Legal">
                <TextRow nullable={false}
                  control={control}
                  name="legalName"
                  label="Legal Name"
                  placeholder="Legal entity name"
                  iconLeft={<FileText />}
                />
                <TextRow nullable={false}
                  control={control}
                  name="registrationNumber"
                  label="Registration Number"
                  placeholder="Registration number"
                  iconLeft={<Hash />}
                />
                <TextRow nullable={false}
                  control={control}
                  name="taxId"
                  label="Tax ID"
                  placeholder="Tax identification number"
                  iconLeft={<Hash />}
                />
              </PrntrCard>
            </form>
          )}
        </div>

        {/* Footer */}
        <DrawerFooter className="flex flex-row gap-2 items-start justify-end p-4">
          <Button
            onClick={onClose}
            variant="secondary"
            size="mini"
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleFormSubmit}
            variant="primary"
            size="mini"
            disabled={isSubmitting || !isDirty || !isValid}
            className="flex-1"
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
